<template>
  <div class="vitamines-mineraux-page">
    <!-- Header / vidéo -->
    <section class="header-section">
      <div class="carousel">
        <div class="carousel-videos" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
          <div v-for="(video, index) in videos" :key="index" class="carousel-video">
            <video
              :src="video"
              autoplay
              muted
              loop
              playsinline
              webkit-playsinline
              id="VideoWorker-0"
              class="video-player"
            ></video>
            <div class="carousel-text">
              <h1 class="page-title">Vitamines & Minéraux</h1>
            </div>
          </div>
        </div>

        <div class="carousel-dots">
          <span
            v-for="(video, index) in videos"
            :key="index"
            :class="{ active: currentIndex === index }"
            class="carousel-dot"
            @click="goToSlide(index)"
          ></span>
        </div>
      </div>
    </section>

    <!-- BANDEAU CYCLE PROFESSIONNEL - SLIDER AUTOMATIQUE -->
    <section class="pro-cycle-section">
      <div class="pro-cycle-container">
        <div class="pro-cycle-header">
          <span class="pro-badge">🏥 CYCLE PROFESSIONNEL</span>
          <span class="disclaimer">⚠️ NON DESTINÉ À LA VENTE - RÉSERVÉ AUX PROFESSIONNELS DE SANTÉ</span>
        </div>
        
        <div class="pro-slider-container">
          <button class="pro-slider-btn prev" @click="prevProSlide">
            ❮
          </button>
          
          <div class="pro-slider-wrapper">
            <div 
              class="pro-slider-track"
              :style="{ transform: `translateX(-${proSliderIndex * 100}%)` }"
            >
              <div 
                v-for="(product, index) in professionalProducts" 
                :key="index" 
                class="pro-slide"
              >
                <div class="pro-cycle-card">
                  <div class="pro-image-wrapper">
                    <img :src="product.image" :alt="product.name" class="pro-product-image" />
                    <div class="pro-overlay">
                      <span class="pro-sample-tag">ÉCHANTILLON PRO</span>
                    </div>
                  </div>
                  <div class="pro-card-content">
                    <h4 class="pro-product-name">{{ product.name }}</h4>
                    <p class="pro-product-desc">{{ product.shortDesc }}</p>
                    <div class="pro-meta">
                      <span class="pro-ref">REF: {{ product.ref }}</span>
                    </div>
                    <div class="pro-warning">
                      ⚠️ Usage professionnel uniquement
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <button class="pro-slider-btn next" @click="nextProSlide">
            ❯
          </button>
        </div>
        
        <div class="pro-slider-dots">
          <span 
            v-for="(_, idx) in professionalProducts" 
            :key="idx"
            :class="{ active: proSliderIndex === idx }"
            class="pro-dot"
            @click="goToProSlide(idx)"
          ></span>
        </div>
      </div>
    </section>

    <div class="breadcrumb">
      <a href="/">Accueil</a> >
      <RouterLink to="/produits">Produits</RouterLink> >
      <RouterLink to="/vitamines-mineraux">Vitamines & Minéraux</RouterLink>
    </div>

    <div class="shop-container">
      <aside class="sidebar">
        <h2>Filtres</h2>
        <div class="filter-options-item">
          <h3>Marque</h3>
          <form>
            <li v-for="filter in filters.marque" :key="filter.value">
              <input type="checkbox" :value="filter.value" v-model="selectedFilters.marque" />
              <span class="label">{{ filter.label }}</span>
              <span class="count">({{ filter.count }})</span>
            </li>
          </form>
        </div>
        <div class="filter-options-item">
          <h3>Prix</h3>
          <input type="range" :min="priceRange.min" :max="priceRange.max" v-model="priceMin" />
          <input type="range" :min="priceRange.min" :max="priceRange.max" v-model="priceMax" />
          <div>{{ priceMin }} - {{ priceMax }} MAD</div>
        </div>
        <div class="filter-options-item">
          <h3>Formulation</h3>
          <form>
            <li v-for="filter in filters.formulation" :key="filter.value">
              <input type="checkbox" :value="filter.value" v-model="selectedFilters.formulation" />
              <span class="label">{{ filter.label }}</span>
              <span class="count">({{ filter.count }})</span>
            </li>
          </form>
        </div>
        <div class="filter-options-item">
          <div class="filter-options-title" role="tab" tabindex="0" @click="toggleFilterContent('forme')">
            <h3>Forme</h3>
          </div>
          <div class="filter-options-content" v-show="isFormeExpanded" role="tabpanel">
            <form autocomplete="off">
              <li v-for="(option, index) in filters.forme" :key="index" class="item">
                <label>
                  <input type="checkbox" :value="option.value" v-model="selectedFilters.forme" />
                  <span class="label">{{ option.label }}</span>
                  <span class="count">({{ option.count }})</span>
                </label>
              </li>
            </form>
          </div>
        </div>
      </aside>

      <main class="products-section">
        <p class="page-description">
          Découvrez notre large gamme de vitamines et de minéraux, essentiels pour le bien-être et la santé.
        </p>

        <div class="toolbar toolbar-products">
          <div class="display-mode">
            <button @click="setDisplayMode('grid')" :class="{ active: displayMode === 'grid' }">
              <i class="fas fa-th-large"></i>
            </button>
            <button @click="setDisplayMode('list')" :class="{ active: displayMode === 'list' }">
              <i class="fas fa-list"></i>
            </button>
          </div>
          <div class="catalogue-download">
            <button @click="downloadCatalogue" class="catalogue-btn">
              Télécharger notre catalogue
            </button>
          </div>
          <div class="field limiter">
            <label for="limiter" class="label"><span>Afficher</span></label>
            <select id="limiter" v-model.number="itemsPerPage" class="limiter-options">
              <option :value="10">10</option>
              <option :value="16">16</option>
              <option :value="22">22</option>
              <option :value="30">30</option>
            </select>
          </div>
          <div class="toolbar-sorter sorter">
            <label for="sorter" class="sorter-label">Trier par</label>
            <select id="sorter" v-model="sortBy" class="sorter-options">
              <option value="name">Produits</option>
              <option value="price">Prix</option>
            </select>
          </div>
        </div>

        <div :class="displayMode === 'grid' ? 'product-grid' : 'product-list'">
          <div class="product-item" v-for="(product, index) in paginatedProducts" :key="index">
            <div v-if="product.sample" class="sample-badge">🧪 Échantillon</div>
            <img :src="product.image" :alt="product.name" class="product-image" />
            <div class="wishlist-icon" @click="toggleWishlist(product)">
              <i :class="isInWishlist(product) ? 'fas fa-heart active' : 'far fa-heart'"></i>
            </div>
            <h3 class="product-name">{{ product.name }}</h3>
            <p class="product-price">{{ product.price }} MAD</p>
            <p v-if="product.sample" class="sample-notice">⚠️ Échantillon - Non destiné à la vente</p>
            <p class="product-description" v-if="displayMode === 'grid'">{{ product.description }}</p>
            <button class="add-to-cart-btn" @click="addToCart(product)">🛒 Ajouter</button>
            <button class="view-details-btn" @click="openModal(product)">Voir détails</button>
          </div>
        </div>
      </main>
    </div>

    <!-- MODAL DÉTAILS PRODUIT AVEC LOUPE -->
    <div v-if="isModalOpen" class="modal" @click.self="handleOverlay">
      <div class="modal-content" role="dialog" aria-modal="true">
        <button class="close-btn" @click="closeModal" aria-label="Fermer">&times;</button>
        
        <div class="modal-body">
          <div class="modal-left">
            <div class="image-zoom-container">
              <div 
                class="image-zoom-wrapper"
                @mousemove="handleZoomMove"
                @mouseleave="handleZoomLeave"
                @mouseenter="handleZoomEnter"
              >
                <img 
                  :src="modalProduct?.image" 
                  :alt="modalProduct?.name" 
                  class="modal-image" 
                  ref="productImage"
                />
                <button class="fullscreen-btn" @click="openFullscreen" title="Agrandir l'image">
                  🔍
                </button>
                <div 
                  class="zoom-lens" 
                  v-if="zoomActive"
                  :style="lensStyle"
                ></div>
              </div>
              <div 
                class="zoom-result" 
                v-if="zoomActive"
                :style="zoomResultStyle"
              >
                <div 
                  class="zoom-result-image"
                  :style="zoomResultImageStyle"
                ></div>
              </div>
            </div>
          </div>
          
          <div class="modal-right">
            <h3 class="modal-title">{{ modalProduct?.name }}</h3>
            <div v-if="modalProduct?.sample" class="sample-alert">⚠️ ÉCHANTILLON GRATUIT - NON DESTINÉ À LA VENTE</div>
            <p class="modal-price"><strong>{{ modalProduct?.price }} MAD</strong></p>
            <div class="modal-desc-full">
              <p class="formatted-desc">{{ modalProduct?.detailedDescription || modalProduct?.description }}</p>
            </div>
            <ul class="modal-meta">
              <li><strong>Marque :</strong> {{ labelForMarque(modalProduct?.marque) }}</li>
              <li><strong>Forme :</strong> {{ labelForForme(modalProduct?.forme) }}</li>
              <li><strong>Formulation :</strong> {{ labelForFormulation(modalProduct?.formulation) }}</li>
            </ul>
            <div class="modal-actions">
              <button class="add-to-cart-btn" @click="addToCart(modalProduct)">Ajouter au panier</button>
              <button class="view-details-btn" @click="closeModal">Fermer</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL PLEIN ÉCRAN POUR L'IMAGE -->
    <div v-if="isFullscreenOpen" class="fullscreen-modal" @click.self="closeFullscreen">
      <div class="fullscreen-content">
        <button class="fullscreen-close" @click="closeFullscreen">&times;</button>
        <img :src="fullscreenImage" :alt="modalProduct?.name" class="fullscreen-image" />
      </div>
    </div>

    <!-- MODAL CONFIRMATION AJOUT PANIER -->
    <div v-if="addConfirmOpen" class="modal" @click.self="closeAddConfirm">
      <div class="confirm-box" role="dialog" aria-modal="true">
        <button class="close-btn" aria-label="Fermer" @click="closeAddConfirm">&times;</button>
        <div class="alert success">
          Le produit <strong>{{ addedProduct?.name }}</strong> a été ajouté à votre panier.
        </div>
        <div class="confirm-actions">
          <button class="btn primary" @click="continueShopping">Continuer ({{ countdown }})</button>
          <button class="btn outline" @click="goToCart">Voir Panier</button>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="showWishlistToast" class="wishlist-toast">{{ wishlistToast }}</div>
    </transition>
  </div>
</template>

<script>
import { store } from "@/Store";
import axios from "axios";
import { wishlistStore } from "@/wishlisteStore";

export default {
  name: "ShopI",
  data() {
    return {
      sortBy: "name",
      itemsPerPage: 10,
      displayMode: "grid",
      isModalOpen: false,
      modalProduct: null,
      currentIndex: 0,
      addConfirmOpen: false,
      addedProduct: null,
      countdown: 3,
      countdownInterval: null,
      wishlist: [],
      wishlistItems: [],
      wishlistToast: null,
      showWishlistToast: false,
      
      // Zoom
      zoomActive: false,
      zoomX: 0,
      zoomY: 0,
      zoomWidth: 120,
      zoomHeight: 120,
      isFullscreenOpen: false,
      fullscreenImage: "",

      videos: [require("@/assets/vedio.mp4")],

      proSliderIndex: 0,
      proSliderInterval: null,
      autoplayDelay: 4000,

      professionalProducts: [
        { id: "pro_propixyl", name: "PROPIXYL®", shortDesc: "Propolis & Thym - Apaise les voies respiratoires", image: "sirop.png", ref: "PRO-PROP-001" },
        { id: "pro_sleepplus", name: "SLEEP PLUS", shortDesc: "Magnésium Marin & Vitamine B6 - Réduit la fatigue", image: "Boîte SLEEP PLUS avec gélules.png", ref: "PRO-SLP-002" },
        { id: "pro_neurocalm", name: "NEUROCALM®", shortDesc: "Complexe apaisant - Bien-être mental", image: "Complément alimentaire Neurocalm en studio.png", ref: "PRO-NRL-003" },
        { id: "pro_miorelax", name: "MIORELAX®", shortDesc: "Melatonine + Magnésium + Vitamine B6 - Endormissement rapide", image: "MIORELAX.png", ref: "PRO-MIO-004" }
      ],

      products: [
           { id: "Levure de Biére", name: "Levure de Biére", description: "Notre levure contribue au métabolisme énergétique normal et à l'absorption intestinale des nutriments.", detailedDescription: "La levure de bière est un complément alimentaire naturel riche en vitamines du groupe B, en protéines et en minéraux.\n\n✨ BIENFAITS :\n\n✓ Aide au maintien d'un système nerveux en bonne santé\n✓ Stimule les fonctions physiques ou intellectuelles\n✓ Contribue à la santé de la peau, des cheveux ou des ongles\n✓ Soutient les défenses immunitaires\n✓ Réduit l'inconfort digestif et soulage la diarrhée\n✓ Soutient le cœur et les vaisseaux sanguins\n✓ Aide à la synthèse des éléments du sang\n\n💊 CONSEILS D'UTILISATION :\nPrendre 2 à 3 comprimés par jour pendant les repas, avec un grand verre d'eau.\n\n📋 COMPOSITION :\nLevure de bière (Saccharomyces cerevisiae), agent de charge : cellulose microcristalline, anti-agglomérant : stéarate de magnésium.\n\n⚠️ PRÉCAUTIONS :\nDéconseillé aux femmes enceintes ou allaitantes sans avis médical. Ne pas dépasser la dose quotidienne recommandée.", price: 0, image: "fermyx.png", marque: "gph", forme: 9320, formulation: "bio", sample: false },
        { id: "Ashwazen", name: "Ashwazen", price: 0, description: "Réduit le stress & l'anxiété, renforce l'immunité, énergie & vitalité.", detailedDescription: "L'Ashwazen (Withania somnifera) est une plante adaptogène utilisée depuis des millénaires en médecine ayurvédique.\n\n BIENFAITS :\n✓ Réduit le stress et l'anxiété\n✓ Renforce le système immunitaire\n✓ Augmente l'énergie et la vitalité\n✓ Améliore la concentration et la mémoire\n✓ Favorise un sommeil réparateur\n\n COMPOSITION :\nAshwazen : 300 mg par gélule\nExcipients : Amidon de maïs, Cellulose de magnésium\n\n POSOLOGIE :\n1 à 2 gélules par jour avec un verre d'eau", image: "ASHWAEZN.png", marque: "gph", forme: 9321, formulation: "bio", sample: false },
        { id: "shilazen", name: "Shilazen - Shilajit", description: "Source naturelle de minéraux - Vitalité & Énergie", detailedDescription: "Le Shilajit est une source naturelle de minéraux et d'acide fulvique. Il aide à augmenter l'énergie, améliorer les performances physiques et soutenir la vitalité générale.\n\n✓ Source naturelle de minéraux\n✓ Augmente l'énergie et l'endurance\n✓ Soutient les fonctions cognitives\n✓ Ralentit le vieillissement cellulaire\n✓ Renforce le système immunitaire\n\nPosologie : 1 gélule par jour\n60 gélules - Poids net : 9g", price: 0, image: "SHILAZEN (2).png", marque: "gph", forme: 9321, formulation: "naturel", sample: false },
        { id: "glycalm", name: "Glycalm - Magnésium Bisglycinate", description: "Réduit la fatigue - Fonction musculaire normale - Équilibre électrolytique", detailedDescription: "Le magnésium bisglycinate est hautement assimilable et contribue à réduire la fatigue, au bon fonctionnement musculaire et à l'équilibre électrolytique.\n\n✓ Réduit la fatigue\n✓ Fonction musculaire normale\n✓ Équilibre électrolytique\n✓ Soutien du système nerveux\n\nComposition : Bisglycinate de magnésium 150 mg par gélule\nPosologie : 1 gélule par jour\n60 gélules - Poids net : 18g", price: 0, image:"glycalm.png", marque: "gph", forme: 9321, formulation: "naturel", sample: false },
        { id: "vitasold", name: "Vitasol D - Vitamine D3", description: "5µg de vitamine D3 - Os solides - Immunité", detailedDescription: "La vitamine D3 est essentielle pour l'absorption du calcium, la santé osseuse et le bon fonctionnement du système immunitaire.\n\n✓ Os solides et en bonne santé\n✓ Soutient le système immunitaire\n✓ Fonction musculaire normale\n✓ Absorption du calcium\n\nComposition : Vitamine D3 5µg par comprimé\nPosologie : 1 comprimé par jour\n60 comprimés - Poids net : 24g", price: 0, image: "vitasol d.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
        { id: "zinelys", name: "Zinelys - Zinc & Vitamine B6", description: "10mg Zinc - 2mg Vitamine B6 - Immunité - Énergie", detailedDescription: "Le Zinc associé à la Vitamine B6 contribue au bon fonctionnement du système immunitaire et au métabolisme énergétique.\n\n✓ Soutient l'immunité\n✓ Métabolisme énergétique\n✓ Peau et cheveux en bonne santé\n✓ Fonction cognitive normale\n\nComposition : Zinc 10mg, Vitamine B6 2mg\nPosologie : 1 comprimé par jour\n90 comprimés - Poids net : 36g", price: 0, image: "zinzlys.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
        { id: "cvantis", name: "Cvantis - Vitamine C", description: "Réduit la fatigue - Protège du stress oxydatif - Soutient l'immunité", detailedDescription: "La Vitamine C est un antioxydant puissant qui contribue à réduire la fatigue, protéger les cellules du stress oxydatif et soutenir le système immunitaire.\n\n✓ Réduit la fatigue\n✓ Protège du stress oxydatif\n✓ Soutient l'immunité\n✓ Collagène et santé de la peau\n\nComposition : Vitamine C 180mg par comprimé\nPosologie : 1 comprimé par jour\n90 comprimés - Poids net : 45g", price: 0, image: "c vantis.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
        { id: "b12via", name: "B12 Via - Vitamine B12", description: "3µg de Vitamine B12 - Énergie - Système nerveux", detailedDescription: "La Vitamine B12 contribue au métabolisme énergétique, au bon fonctionnement du système nerveux et à la formation des globules rouges.\n\n✓ Métabolisme énergétique\n✓ Système nerveux en bonne santé\n✓ Formation des globules rouges\n✓ Réduction de la fatigue\n\nComposition : Vitamine B12 3µg par comprimé\nPosologie : 1 comprimé par jour\n90 comprimés - Poids net : 36g", price: 0, image:"b12.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
        { id: "apivia", name: "Apivia - Gelée Royale & Pollen", description: "Renforce l'organisme - Tonus & Vitalité", detailedDescription: "Apivia associe la gelée royale, le pollen et la vitamine C pour renforcer les défenses naturelles, réduire la fatigue et soutenir le système immunitaire.\n\n✓ Renforce l'organisme\n✓ Tonus et vitalité\n✓ Réduit la fatigue\n✓ Soutient l'immunité\n\nComposition : Gelée royale 155mg, Pollen 110mg, Vitamine C 60mg\nPosologie : 2 à 3 gélules par jour\n60 gélules - Poids net : 24g", price: 0, image: "APIVIA.png", marque: "gph", forme: 9321, formulation: "naturel", sample: false },
        { id: "spiralis", name: "Spiralis - Spiruline", description: "Vitalité naturelle & Énergie durable", detailedDescription: "La spiruline est une micro-algue riche en protéines, vitamines et minéraux qui apporte vitalité naturelle et énergie durable.\n\n✓ Vitalité naturelle\n✓ Énergie durable\n✓ Riche en nutriments essentiels\n✓ Soutient l'immunité\n\nComposition : Spiruline 500mg par comprimé\nPosologie : 1 à 2 comprimés par jour\n240 comprimés - Poids net : 120g", price: 0, image: "SPIRALIS.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
        { id: "collazen", name: "Collazen - Collagène & Vitamine C", description: "Éclat & Vitalité - Peau - Articulations", detailedDescription: "Le Collagène associé à la Vitamine C contribue à la santé de la peau, des articulations et à la vitalité générale.\n\n✓ Éclat de la peau\n✓ Vitalité générale\n✓ Soutient les articulations\n✓ Renforce les cheveux et ongles\n\nPosologie : 1 comprimé par jour\n90 comprimés - Poids net : 36g", price: 0, image: "COLLAZEN.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
        { id: "supraslim", name: "Supraslim - Brûle Graisses", description: "Thé vert - Café vert - Vitamines B6 & B12", detailedDescription: "Supraslim est un complément brûle-graisses à base de thé vert, café vert, vitamines B6 et B12 pour aider à la gestion du poids.\n\n✓ Brûle efficacement les graisses\n✓ Thé vert antioxydant\n✓ Café vert métabolisme\n✓ Vitamines B pour l'énergie\n\nPosologie : 2 gélules par jour\n90 gélules - Poids net : 40g", price: 0, image: "SUPRASLIM.png", marque: "gph", forme: 9321, formulation: "naturel", sample: false },
        { id: "biotinat", name: "Biotinat - Peau Cheveux Ongles", description: "Biotine - Zinc - Sélénium - Nutrition essentielle pour la beauté", detailedDescription: "Biotinat associe la Biotine, le Zinc et le Sélénium pour une nutrition complète de la peau, des cheveux et des ongles.\n\n✓ Peau éclatante\n✓ Cheveux renforcés\n✓ Ongles plus solides\n✓ Nutrition essentielle\n\nPosologie : 1 comprimé par jour\n90 comprimés - Poids net : 144g", price: 0, image: "BIOTINAT.png", marque: "gph", forme: 9320, formulation: "naturel", sample: false },
       { 
  id: "morivera", 
  name: "MORIVERA", 
  price: 0, 
  description: "Complément alimentaire à base de Moringa - Source naturelle d'antioxydants, de vitamine C, de polyphénols et de bêta-carotènes.", 
  detailedDescription: "MORIVERA est un complément alimentaire à base de feuilles de Moringa oleifera, une plante récoltée pour sa richesse naturelle en nutriments essentiels.\n\n✨ BIENFAITS :\n\n✓ Source naturelle d'antioxydants\n✓ Riche en vitamine C\n✓ Contient des polyphénols et bêta-carotènes\n✓ Aide à assurer la santé et le bien-être général\n✓ Utile en cas de carence alimentaire\n✓ Soutient les défenses naturelles\n\n💊 COMPOSITION :\nMoringa (Moringa oleifera) : 500 mg par gélule\nExcipient : Gélule en gélatine\n\n💊 CONSEILS D'UTILISATION :\n1 gélule à 3 fois par jour avec un grand verre d'eau.\n\n⚠️ PRÉCAUTIONS :\nNe pas dépasser la dose recommandée.\nLes compléments alimentaires ne doivent pas remplacer une alimentation variée et équilibrée.\nEn cas de grossesse, d'allaitement ou de problèmes de médicaments, consulter un spécialiste.\nConserver dans un endroit frais et sec à l'abri de la lumière.\nTenir hors de portée des enfants.", 
  image: "MORIVERA.png", 
  marque: "gph", 
  forme: 9321, 
  formulation: "naturel", 
  sample: false 
}
      ],

      filters: {
        marque: [{ label: "HOLISTEA", value: "gph", count: 4 }],
        formulation: [{ label: "Certifié Bio", value: "bio", count: 2 }],
        forme: [
          { label: "Comprimés", value: 9320, count: 1 },
          { label: "Gélules", value: 9321, count: 3 },
        ],
      },

      priceRange: { min: 0, max: 1000 },
      priceMin: 0,
      priceMax: 1000,

      selectedFilters: { marque: [], formulation: [], forme: [] },
      isFormeExpanded: true,
      interval: null,
    };
  },

  computed: {
    filteredProducts() {
      let list = this.products.filter((product) => {
        const matchesPrice = product.price >= this.priceMin && product.price <= this.priceMax;
        const matchesMarque = !this.selectedFilters.marque.length || this.selectedFilters.marque.includes(product.marque);
        const matchesFormulation = !this.selectedFilters.formulation.length || this.selectedFilters.formulation.includes(product.formulation);
        const matchesForme = !this.selectedFilters.forme.length || this.selectedFilters.forme.includes(product.forme);
        return matchesPrice && matchesMarque && matchesFormulation && matchesForme;
      });
      if (this.sortBy === "name") {
        list.sort((a, b) => a.name.localeCompare(b.name));
      } else if (this.sortBy === "price") {
        list.sort((a, b) => a.price - b.price);
      }
      return list;
    },
    paginatedProducts() {
      return this.filteredProducts.slice(0, this.itemsPerPage);
    },
    cartCount() {
      return store.cart.reduce((sum, it) => sum + (it.quantity || 1), 0);
    },
    
    lensStyle() {
      return {
        left: `${this.zoomX}px`,
        top: `${this.zoomY}px`,
        width: `${this.zoomWidth}px`,
        height: `${this.zoomHeight}px`
      };
    },
    
    zoomResultStyle() {
      const img = this.$refs.productImage;
      const imgWidth = img ? img.offsetWidth : 280;
      const imgHeight = img ? img.offsetHeight : 280;
      const scale = 2;
      return {
        width: `${this.zoomWidth * scale}px`,
        height: `${this.zoomHeight * scale}px`,
        backgroundImage: `url(${this.modalProduct?.image})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: `-${this.zoomX * scale}px -${this.zoomY * scale}px`,
        backgroundSize: `${imgWidth * scale}px ${imgHeight * scale}px`
      };
    },
    
    zoomResultImageStyle() {
      const img = this.$refs.productImage;
      const imgWidth = img ? img.offsetWidth : 280;
      const imgHeight = img ? img.offsetHeight : 280;
      const scale = 2;
      return {
        width: "100%",
        height: "100%",
        backgroundImage: `url(${this.modalProduct?.image})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: `-${this.zoomX * scale}px -${this.zoomY * scale}px`,
        backgroundSize: `${imgWidth * scale}px ${imgHeight * scale}px`
      };
    }
  },

  async mounted() {
    const video = document.getElementById("VideoWorker-0");
    if (video) {
      video.muted = true;
      video.loop = true;
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.controls = false;
      video.play().catch(() => {});
    }
    window.addEventListener("keydown", this.onKeydown);
    await this.fetchWishlist();
    this.startAutoplay();
  },

  beforeUnmount() {
    if (this.addConfirmTimer) clearTimeout(this.addConfirmTimer);
    window.removeEventListener("keydown", this.onKeydown);
    this.stopAutoplay();
  },

  methods: {
    startAutoplay() {
      this.stopAutoplay();
      this.proSliderInterval = setInterval(() => {
        this.nextProSlide();
      }, this.autoplayDelay);
    },
    stopAutoplay() {
      if (this.proSliderInterval) {
        clearInterval(this.proSliderInterval);
        this.proSliderInterval = null;
      }
    },
    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    prevProSlide() {
      this.proSliderIndex = (this.proSliderIndex - 1 + this.professionalProducts.length) % this.professionalProducts.length;
      this.resetAutoplay();
    },
    nextProSlide() {
      this.proSliderIndex = (this.proSliderIndex + 1) % this.professionalProducts.length;
      this.resetAutoplay();
    },
    goToProSlide(index) {
      this.proSliderIndex = index;
      this.resetAutoplay();
    },
    downloadCatalogue() {
      const catalogueUrl = '/CATALOGUEHOLISTEA42026.pdf';
      const link = document.createElement('a');
      link.href = catalogueUrl;
      link.download = 'CATALOGUE HOLISTEA 2026.pdf';
      link.click();
    },
    toggleFilterContent(key) {
      if (key === "forme") this.isFormeExpanded = !this.isFormeExpanded;
    },
    setDisplayMode(mode) {
      this.displayMode = mode;
    },
    addToCart(product) {
      store.addToCart(product);
      this.addedProduct = product;
      this.addConfirmOpen = true;
      document.body.style.overflow = 'hidden';
      this.startAutoDismiss();
    },
    startAutoDismiss() {
      this.clearCountdown();
      this.countdown = 3;
      this.countdownInterval = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) this.closeAddConfirm();
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    continueShopping() { this.closeAddConfirm(); },
    goToCart() {
      this.closeAddConfirm();
      this.$router.push('/panier');
    },
    closeAddConfirm() {
      this.clearCountdown();
      this.addConfirmOpen = false;
      this.addedProduct = null;
      document.body.style.overflow = '';
    },
    closeModal() {
      this.isModalOpen = false;
      this.modalProduct = null;
      this.zoomActive = false;
      document.body.style.overflow = "";
    },
    handleOverlay(e) {
      if (e.target === e.currentTarget) this.closeModal();
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.isModalOpen) this.closeModal();
        if (this.addConfirmOpen) this.closeAddConfirm();
        if (this.isFullscreenOpen) this.closeFullscreen();
      }
    },
    labelForMarque(value) {
      const m = this.filters.marque.find((x) => x.value === value);
      return m ? m.label : value;
    },
    labelForForme(value) {
      const f = this.filters.forme.find((x) => x.value === value);
      return f ? f.label : value;
    },
    labelForFormulation(value) {
      const f = this.filters.formulation.find((x) => x.value === value);
      return f ? f.label : value;
    },
    goToSlide(i) { this.currentIndex = i; },
    async fetchWishlist() {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) return;
        const res = await axios.get("http://localhost:2028/api/wishlist", {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.wishlistItems = res.data || [];
      } catch (err) {
        console.warn("Erreur récupération wishlist:", err);
      }
    },
    isInWishlist(product) {
      return this.wishlistItems.some(item => item.productId === product._id || item.productId === product.id);
    },
    async toggleWishlist(product) {
      const token = sessionStorage.getItem("token");
      if (!token) {
        alert("Connectez-vous pour utiliser la wishlist ❤️");
        this.$router.push("/connexion");
        return;
      }
      try {
        if (this.isInWishlist(product)) {
          await axios.delete(`http://localhost:2028/api/wishlist/${product._id || product.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          this.wishlistItems = this.wishlistItems.filter(p => p.productId !== (product._id || product.id));
          wishlistStore.decrement();
          this.triggerWishlistToast("Produit retiré de votre wishlist 💔");
        } else {
          await axios.post("http://localhost:2028/api/wishlist", {
            productId: product._id || product.id,
            name: product.name,
            price: product.price,
            image: product.image,
          }, { headers: { Authorization: `Bearer ${token}` } });
          this.wishlistItems.push({ productId: product._id || product.id, name: product.name, price: product.price, image: product.image });
          wishlistStore.increment();
          this.triggerWishlistToast("Produit ajouté à votre wishlist ❤️");
        }
      } catch (err) {
        console.error("Erreur wishlist:", err);
      }
    },
    triggerWishlistToast(message) {
      this.wishlistToast = message;
      this.showWishlistToast = true;
      setTimeout(() => { this.showWishlistToast = false; }, 2000);
    },

    // Méthodes de zoom
    handleZoomMove(event) {
      if (!this.modalProduct) return;
      
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;
      
      let lensX = offsetX - this.zoomWidth / 2;
      let lensY = offsetY - this.zoomHeight / 2;
      
      if (lensX < 0) lensX = 0;
      if (lensY < 0) lensY = 0;
      if (lensX + this.zoomWidth > rect.width) lensX = rect.width - this.zoomWidth;
      if (lensY + this.zoomHeight > rect.height) lensY = rect.height - this.zoomHeight;
      
      this.zoomX = lensX;
      this.zoomY = lensY;
    },
    
    handleZoomEnter() {
      if (!this.modalProduct) return;
      this.zoomActive = true;
    },
    
    handleZoomLeave() {
      this.zoomActive = false;
    },
    
    openFullscreen() {
      if (this.modalProduct) {
        this.fullscreenImage = this.modalProduct.image;
        this.isFullscreenOpen = true;
        document.body.style.overflow = "hidden";
      }
    },
    
    closeFullscreen() {
      this.isFullscreenOpen = false;
      this.fullscreenImage = "";
      document.body.style.overflow = "";
    },
    
    openModal(product) {
      this.modalProduct = product;
      this.isModalOpen = true;
      this.zoomActive = false;
      document.body.style.overflow = "hidden";
    },
  },
};
</script>

<style scoped>
/* ====== CYCLE PROFESSIONNEL - SLIDER AUTOMATIQUE ====== */
.pro-cycle-section {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 20px;
  margin: 0;
  position: relative;
  overflow: hidden;
}

.pro-cycle-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #ff6b35, #ffd700, #ff6b35);
}

.pro-cycle-container {
  max-width: 1400px;
  margin: 0 auto;
}

.pro-cycle-header {
  text-align: center;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.pro-badge {
  display: inline-block;
  background: #ff6b35;
  color: white;
  padding: 4px 18px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 0.75rem;
  letter-spacing: 1px;
  align-self: center;
}

.disclaimer {
  display: inline-block;
  background: rgba(255, 107, 53, 0.12);
  color: #ffaa88;
  padding: 3px 14px;
  border-radius: 50px;
  font-size: 0.6rem;
  font-weight: 600;
}

.pro-slider-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
}

.pro-slider-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 107, 53, 0.5);
  color: #ff6b35;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  z-index: 10;
  flex-shrink: 0;
}

.pro-slider-btn:hover {
  background: #ff6b35;
  color: white;
  border-color: #ff6b35;
  transform: scale(1.05);
}

.pro-slider-btn.prev { margin-right: 10px; }
.pro-slider-btn.next { margin-left: 10px; }

.pro-slider-wrapper {
  flex: 1;
  overflow: hidden;
  max-width: 300px;
  margin: 0 auto;
}

.pro-slider-track {
  display: flex;
  transition: transform 0.5s ease-in-out;
}

.pro-slide {
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
}

.pro-cycle-card {
  width: 260px;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.pro-cycle-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.pro-image-wrapper {
  position: relative;
  height: 110px;
  overflow: hidden;
  background: #f5f5f5;
}

.pro-product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
  transition: transform 0.3s ease;
}

.pro-cycle-card:hover .pro-product-image { transform: scale(1.03); }

.pro-overlay {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 2;
}

.pro-sample-tag {
  background: #ff6b35;
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.6rem;
  font-weight: bold;
}

.pro-card-content {
  padding: 10px 12px;
}

.pro-product-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 5px 0;
  text-align: center;
}

.pro-product-desc {
  font-size: 0.65rem;
  color: #666;
  margin: 0 0 8px 0;
  line-height: 1.4;
  text-align: center;
}

.pro-meta {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 0.6rem;
}

.pro-ref { color: #ff6b35; font-weight: 600; }

.pro-warning {
  background: #fff3e6;
  color: #ff6b35;
  padding: 5px;
  border-radius: 6px;
  font-size: 0.6rem;
  text-align: center;
  font-weight: 600;
}

.pro-slider-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 15px;
}

.pro-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
}

.pro-dot.active {
  background: #ff6b35;
  width: 20px;
  border-radius: 4px;
}

/* RESPONSIVE SLIDER */
@media (max-width: 768px) {
  .pro-cycle-section { padding: 15px; }
  .pro-cycle-card { width: 220px; }
  .pro-image-wrapper { height: 95px; }
  .pro-product-name { font-size: 0.75rem; }
  .pro-product-desc { font-size: 0.55rem; }
  .pro-badge { font-size: 0.65rem; padding: 3px 14px; }
  .disclaimer { font-size: 0.55rem; }
  .pro-slider-btn { width: 32px; height: 32px; font-size: 1rem; }
  .pro-slider-wrapper { max-width: 240px; }
}

@media (max-width: 480px) {
  .pro-cycle-card { width: 200px; }
  .pro-image-wrapper { height: 85px; }
  .pro-product-name { font-size: 0.7rem; }
  .pro-product-desc { font-size: 0.5rem; }
  .pro-warning { font-size: 0.5rem; }
  .pro-slider-btn { width: 28px; height: 28px; font-size: 0.8rem; }
  .pro-slider-wrapper { max-width: 210px; }
}

/* ====== BADGE ÉCHANTILLON ====== */
.sample-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #ff6b35;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 20px;
  z-index: 10;
}

.sample-notice {
  font-size: 0.75rem;
  color: #ff6b35;
  font-weight: 500;
  margin: 5px 0;
  background: #fff3e6;
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
}

.sample-alert {
  background: #fff3e6;
  border-left: 4px solid #ff6b35;
  padding: 10px 12px;
  margin: 10px 0;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: bold;
  color: #cc5a2a;
  text-align: center;
}

/* ====== BOUTON CATALOGUE ====== */
.catalogue-download { margin: 15px 0; text-align: center; }
.catalogue-btn {
  background: linear-gradient(135deg, #00998B, #007c71);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.catalogue-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0,153,139,0.3);
  background: linear-gradient(135deg, #00bfa5, #00998B);
}
.toolbar-products .catalogue-download { margin: 0; }
.toolbar-products .catalogue-btn { padding: 8px 16px; font-size: 0.85rem; }

/* ====== MODAL AVEC LOUPE ====== */
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}

.modal-content {
  background: #fff;
  width: 100%;
  max-width: 920px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  position: relative;
}

.modal-content::-webkit-scrollbar { width: 6px; }
.modal-content::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
.modal-content::-webkit-scrollbar-thumb { background: #00998B; border-radius: 10px; }

.close-btn {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: #f1f1f1;
  color: #222;
  font-size: 22px;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
}
.close-btn:hover { background: #e7e7e7; transform: scale(1.05); }

.modal-body {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 20px;
  padding: 24px;
}

.modal-left { display: flex; align-items: center; justify-content: center; }
.modal-right { display: flex; flex-direction: column; }

.modal-title {
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 700;
  color: #1d1d1f;
}

.modal-price {
  margin: 0 0 12px 0;
  font-size: 20px;
  color: #00998B;
  font-weight: bold;
}

.formatted-desc {
  white-space: pre-line;
  line-height: 1.7;
  color: #4a5568;
  font-size: 14px;
  margin: 0 0 15px 0;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 10px;
}

.formatted-desc::-webkit-scrollbar { width: 4px; }
.formatted-desc::-webkit-scrollbar-track { background: #e0e0e0; border-radius: 10px; }
.formatted-desc::-webkit-scrollbar-thumb { background: #00998B; border-radius: 10px; }

.modal-meta {
  list-style: none;
  padding: 12px 15px;
  margin: 0 0 20px 0;
  background: #f5f5f5;
  border-radius: 10px;
}

.modal-meta li {
  margin: 8px 0;
  color: #334;
  font-size: 14px;
}

.modal-meta li strong {
  color: #1a1a2e;
  min-width: 100px;
  display: inline-block;
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 10px;
}

.modal-actions .add-to-cart-btn,
.modal-actions .view-details-btn {
  padding: 12px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.modal-actions .add-to-cart-btn {
  background: #00998B;
  color: #fff;
  flex: 2;
}
.modal-actions .add-to-cart-btn:hover { background: #007c71; transform: translateY(-2px); }

.modal-actions .view-details-btn {
  background: #eef2f4;
  color: #222;
  flex: 1;
}
.modal-actions .view-details-btn:hover { background: #e3e7ea; transform: translateY(-2px); }

/* ====== STYLES POUR LA LOUPE ET LE ZOOM ====== */
.image-zoom-container {
  position: relative;
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.image-zoom-wrapper {
  position: relative;
  display: inline-block;
  cursor: crosshair;
  border-radius: 12px;
  overflow: hidden;
}

.modal-image {
  width: 100%;
  max-width: 280px;
  border-radius: 12px;
  object-fit: contain;
  background: #fafafa;
  transition: transform 0.2s ease;
}

.zoom-lens {
  position: absolute;
  border: 2px solid #ff6b35;
  background: rgba(255, 107, 53, 0.2);
  cursor: crosshair;
  border-radius: 50%;
  pointer-events: none;
  box-shadow: 0 0 5px rgba(0,0,0,0.2);
  transition: all 0.05s linear;
}

.zoom-result {
  border: 2px solid #00998B;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 15px rgba(0,0,0,0.15);
  position: relative;
}

.zoom-result-image {
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
}

.fullscreen-btn {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 153, 139, 0.8);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
}
.fullscreen-btn:hover { background: #00998B; transform: scale(1.1); }

/* Modal plein écran pour l'image */
.fullscreen-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  cursor: pointer;
}

.fullscreen-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.fullscreen-image {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.fullscreen-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.fullscreen-close:hover { background: #ff6b35; transform: scale(1.1); }

/* ====== MODAL RESPONSIVE ====== */
@media (max-width: 860px) {
  .modal { padding: 12px; }
  .modal-body { grid-template-columns: 1fr; padding: 20px; gap: 16px; }
  .modal-left { order: 1; max-height: 200px; }
  .modal-image { max-width: 180px; max-height: 180px; }
  .modal-right { order: 2; }
  .modal-title { font-size: 20px; text-align: center; }
  .modal-price { font-size: 18px; text-align: center; }
  .formatted-desc { font-size: 13px; max-height: 250px; }
  .modal-meta li { font-size: 13px; display: flex; flex-wrap: wrap; }
  .modal-meta li strong { min-width: 85px; }
  .modal-actions { flex-direction: row; gap: 10px; }
  .modal-actions button { padding: 10px 16px; font-size: 13px; }
  .close-btn { top: 8px; right: 8px; width: 32px; height: 32px; font-size: 20px; }
  
  /* Cacher le résultat du zoom sur tablette */
  .zoom-result { display: none; }
  .image-zoom-container { flex-direction: column; align-items: center; }
  .image-zoom-wrapper { width: 100%; display: flex; justify-content: center; }
  .fullscreen-btn { right: 50%; transform: translateX(50%); bottom: 5px; }
  .fullscreen-btn:hover { transform: translateX(50%) scale(1.1); }
}

@media (max-width: 480px) {
  .modal { padding: 8px; }
  .modal-body { padding: 16px; gap: 12px; }
  .modal-left { max-height: 160px; }
  .modal-image { max-width: 140px; max-height: 140px; }
  .modal-title { font-size: 18px; }
  .modal-price { font-size: 16px; }
  .formatted-desc { font-size: 12px; max-height: 200px; }
  .modal-meta { padding: 8px 10px; }
  .modal-meta li { font-size: 11px; flex-direction: column; }
  .modal-meta li strong { min-width: auto; margin-bottom: 2px; }
  .modal-actions { flex-direction: column; gap: 8px; }
  .modal-actions button { width: 100%; padding: 10px; }
  .modal-actions .add-to-cart-btn,
  .modal-actions .view-details-btn { flex: auto; }
  .close-btn { top: 5px; right: 5px; width: 28px; height: 28px; font-size: 18px; }
  .zoom-lens { width: 80px !important; height: 80px !important; }
  .fullscreen-btn { width: 30px; height: 30px; font-size: 1rem; }
  .fullscreen-close { width: 32px; height: 32px; font-size: 20px; top: -35px; }
}

/* ====== EXISTANTS ====== */
.shop-container {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 20px;
  padding: 20px;
}

.carousel { position: relative; overflow: hidden; width: 100%; height: 300px; }
.carousel-video { position: relative; min-width: 100%; display: flex; align-items: center; justify-content: center; z-index: 1!important; }
.video-player { width: 100%; height: 100%; object-fit: cover; border-radius: 0; pointer-events: none; }
.carousel-text {
  position: absolute; top: 10%; left: 50%; transform: translateX(-50%);
  color: #333; background: rgba(247,247,250,.86);
  padding: 0 30px; font-size: 24px; text-align: center; z-index: 2;
  width: 70%; box-sizing: border-box; border-radius: 15px;
}
.sidebar { background: #f4f4f4; padding: 20px; }
.filter-options-item { margin-bottom: 20px; }
.filter-options-item h3 { font-size: 1.2em; margin-bottom: 10px; }
.filter-options-item form li { list-style: none; margin-bottom: 10px; }
.vitamines-mineraux-page { padding: 0; font-family: 'Poppins', sans-serif; }
.toolbar-products { display: flex; justify-content: space-between; margin: 0 20px 40px; }
.display-mode button { background: #fff; border: 1px solid #ddd; padding: 10px; margin-right: 10px; cursor: pointer; transition: .3s; }
.display-mode button.active { background: #00998B; color: #fff; }
.field.limiter { display: flex; gap: 10px; align-items: center; font-size: 16px; }
.toolbar-sorter { display: flex; gap: 10px; align-items: center; font-size: 16px; }
.sorter-options { padding: 5px; background: #f4f4f4; border: 1px solid #ddd; border-radius: 5px; font-size: 10px; color: #2c3e50; cursor: pointer; }
.product-list .product-image { width: 20%; height: auto; }
.header-section { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 0; }
.page-title { font-size: 2.5em; margin-bottom: 10px; }
.breadcrumb { font-size: 10px; color: #888; padding: 10px 50px; }
.breadcrumb a { color: #1c1c1d; text-decoration: none; transition: .3s; }
.breadcrumb a:hover { color: #0056b3; }
.page-description { font-size: 18px; color: #2c3e50; text-align: center; margin-bottom: 30px; }
.products-section { margin-top: 20px; text-align: center; }
.product-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; padding: 0 20px; }
.product-item { position: relative; background: #f9f9f9; padding: 20px; border-radius: 8px; box-shadow: 0 2px 6px rgba(0,0,0,.1); text-align: center; transition: transform .3s; }
.product-item:hover { transform: translateY(-8px); }
.product-image { width: 100%; height: auto; border-radius: 8px; margin-bottom: 15px; }
.product-name { font-size: 1.2em; color: #2c3e50; }
.product-price { font-size: 1.1em; color: #27ae60; font-weight: bold; margin: 10px 0; }
.product-description { font-size: .95em; color: #7f8c8d; margin-bottom: 15px; }
.add-to-cart-btn, .view-details-btn { background: #00998B; color: #fff; border: none; padding: 10px 15px; margin: 5px; border-radius: 5px; cursor: pointer; transition: background-color .3s; font-size: .9em; }
.add-to-cart-btn:hover, .view-details-btn:hover { background: #00796B; }

@media (max-width: 1024px) {
  .shop-container { grid-template-columns: 1fr; }
  .sidebar { display: none; }
  .carousel-text { font-size: 18px; width: 90%; }
  .toolbar-products { flex-direction: column; align-items: center; gap: 10px; }
  .product-grid { grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
  .page-title { font-size: 2em; }
  .product-item { padding: 15px; }
  .add-to-cart-btn, .view-details-btn { padding: 8px 12px; font-size: .8em; }
}
@media (max-width: 480px) {
  .carousel-text { font-size: 16px; }
  .product-grid { grid-template-columns: 1fr; }
  .page-title { font-size: 1.8em; }
  .product-item { padding: 10px; }
  .add-to-cart-btn, .view-details-btn { padding: 6px 10px; font-size: .7em; }
}

.confirm-box { background: #fff; width: min(540px, 92vw); border-radius: 10px; padding: 20px; position: relative; box-shadow: 0 20px 60px rgba(0,0,0,.25); }
.alert.success { background: #e8f5e9; color: #1b5e20; border: 1px solid #c8e6c9; padding: 12px; border-radius: 6px; text-align: center; margin-bottom: 14px; }
.confirm-actions { display: flex; gap: 16px; justify-content: center; margin-top: 6px; flex-wrap: wrap; }
.btn.primary { background: #00998B; color: #fff; border: none; padding: 10px 18px; border-radius: 8px; cursor: pointer; font-weight: 600; }
.btn.primary:hover { background: #007c71; }
.btn.outline { background: #fff; color: #00998B; border: 2px solid #00998B; padding: 10px 18px; border-radius: 8px; cursor: pointer; font-weight: 600; }
.btn.outline:hover { background: #f3fffd; }

/* Mode sombre */
@media (prefers-color-scheme: dark) {
  .pro-cycle-card { background: #1e2430; }
  .pro-product-name { color: #aef7f0; }
  .pro-product-desc { color: #9fa8b1; }
  .pro-warning { background: #2a1a0f; color: #ff9e6d; }
  .sample-badge { background: #ff7b4a; color: #1a1a1a; }
  .sample-notice { background: #2a1a0f; color: #ff9e6d; }
  .sample-alert { background: #2a1a0f; border-left-color: #ff7b4a; color: #ff9e6d; }
  body, .shop-container, .vitamines-mineraux-page { background-color: #0f141b !important; color: #e6edf1 !important; }
  .sidebar { background: #161d27 !important; color: #e6edf1 !important; }
  .filter-options-item h3 { color: #00bfa5 !important; }
  .modal-content { background: #10151c; color: #e6edf1; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8); }
  .modal-title { color: #aef7f0; }
  .modal-price { color: #00bfa5; }
  .formatted-desc { color: #cdd4da; }
  .modal-meta { background: #1a1f29; }
  .modal-meta li { color: #9fa8b1; }
  .modal-meta li strong { color: #aef7f0; }
  .modal-image { background: #18222e; }
  .close-btn { background: rgba(255, 255, 255, 0.08); color: #e6edf1; }
  .close-btn:hover { background: rgba(255, 255, 255, 0.15); }
  .modal-actions .add-to-cart-btn { background: #00998B; color: #fff; }
  .modal-actions .add-to-cart-btn:hover { background: #00bfa5; }
  .modal-actions .view-details-btn { background: #1a2532; color: #e6edf1; }
  .modal-actions .view-details-btn:hover { background: #243546; }
  .product-item { background: #1a1f29; color: #dce3ea; box-shadow: 0 2px 8px rgba(0,0,0,.6); }
  .product-name { color: #aef7f0; }
  .product-price { color: #00bfa5; }
  .product-description { color: #9fa8b1; }
  .add-to-cart-btn, .view-details-btn { background: #00998B; color: #fff; }
  .toolbar-products, .page-description, .breadcrumb a, .display-mode button { color: #e6edf1 !important; background-color: transparent !important; }
  .display-mode button.active { background: #00998B; color: #fff; }
  .sorter-options { background: #1f2833; color: #d1dae3; border-color: #2e3a49; }
  .confirm-box { background: #10151c; color: #e6edf1; }
  .alert.success { background: #113a26; color: #bdf0cc; border-color: #1f6845; }
  .btn.outline { background: transparent; color: #aef7f0; border: 2px solid #00998B; }
  .zoom-result { background: #1e2430; border-color: #00bfa5; }
  .fullscreen-btn { background: rgba(0, 153, 139, 0.7); }
  .fullscreen-btn:hover { background: #00998B; }
  .fullscreen-modal { background: rgba(0, 0, 0, 0.97); }
}

/* Wishlist */
.wishlist-icon { position: absolute; top: 10px; right: 12px; font-size: 1.6em; cursor: pointer; color: #b8b8b8; z-index: 10; transition: transform 0.25s ease, color 0.25s ease; }
.wishlist-icon:hover { transform: scale(1.15); color: #ff6b81; }
.wishlist-icon .fa-heart.active { color: #e0245e; animation: heartPop 0.3s ease-in-out; }
@keyframes heartPop { 0% { transform: scale(0.8); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
.wishlist-toast { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: #00998B; color: #fff; padding: 12px 20px; border-radius: 8px; font-weight: 500; font-size: 1rem; z-index: 9999; animation: bounceIn 0.3s ease-out; }
@keyframes bounceIn { 0% { transform: translate(-50%, 50px); opacity: 0; } 60% { transform: translate(-50%, -5px); opacity: 1; } 100% { transform: translate(-50%, 0); opacity: 1; } }
.fade-enter-active, .fade-leave-active { transition: opacity 0.4s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>